import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './WorkOrders.css';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import { addJobTotal } from "../utils/addJobTotal";


const WorkOrders = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  const [usersList, setUsersList] = useState([]);
  const [jobTotals, setJobTotals] = useState({});


  // NEW: filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchScopeOnly, setSearchScopeOnly] = useState(false);
  const [selectedSite, setSelectedSite] = useState('ALL_SITES');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | Active | Completed | Invoice Sent | On Hold
  const [sortOption, setSortOption] = useState('');
  const [showTotalPopup, setShowTotalPopup] = useState(false);
const [totalInput, setTotalInput] = useState("");


  const [filteredJobs, setFilteredJobs] = useState([]);
  const navigate = useNavigate();

  const states = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
  ];

  const serviceTypes = [
    "Proposal","Meeting","Construction","Maintenance","Action Items","Cleaning","Bulk Trash","Inspection","Electrical","Plumbing","HVAC","Concrete"
  ];

  const statusOptions = ["Active","Completed","Invoice Sent","On Hold"];

  const timeWindowOptions = [
    "ANY",
    "8AM - 10AM",
    "9AM - 11AM",
    "10AM - 12PM",
    "11AM - 1PM",
    "12PM - 2PM",
    "1PM - 3PM",
    "2PM - 4PM",
    "3PM - 5PM",
    "4PM - 6PM",
    "Custom Time"
  ];
  

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsRef = collection(db, 'jobs');
        const addItemRef = collection(db, 'addItem');
        const jobsSnapshot = await getDocs(jobsRef);
        const addItemSnapshot = await getDocs(addItemRef);

        const jobsData = jobsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const addItemData = addItemSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        setJobs([...jobsData, ...addItemData]);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        const userList = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setUsersList(userList);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchJobs();
    fetchUsers();
  }, []);

  // Unique site list for dropdown
  const siteOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach(j => {
      const n = (j.siteName || '').trim();
      if (n) set.add(n);
    });
    return ['ALL_SITES', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [jobs]);

  useEffect(() => {
    filterAndSortJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, searchTerm, searchScopeOnly, selectedSite, statusFilter, sortOption]);

  const closeModal = () => setSelectedJob(null);
  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);
  const closeDeletePopup = () => setShowDeletePopup(false);

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const uploadFiles = async (files, jobId) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${jobId}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const saveNote = async () => {
    if (!selectedJob || (noteText.trim() === "" && selectedFiles.length === 0)) return;

    setUploading(true);
    try {
      let fileUploads = [];
      if (selectedFiles.length > 0) {
        const storage = getStorage();
        fileUploads = await Promise.all(
          selectedFiles.map(async (file) => {
            const fileRef = ref(storage, `notes/${selectedJob.id}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            return { fileURL: url, fileName: file.name };
          })
        );
      }

      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: "User",
        files: fileUploads
      };

      const jobDocRef = doc(db, 'jobs', selectedJob.id);
      const updatedNotes = Array.isArray(selectedJob.notes)
        ? [...selectedJob.notes, newNote]
        : [newNote];

      await updateDoc(jobDocRef, { notes: updatedNotes });

      setSelectedJob((prev) => ({ ...prev, notes: updatedNotes }));
      setNoteText('');
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (err) {
      console.error("Error saving note:", err);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedJob({
      ...selectedJob,
      techAssign: Array.isArray(selectedJob.techAssign)
        ? selectedJob.techAssign
        : [selectedJob.techAssign || ""],
      startTimeMode: selectedJob.startTime?.includes(":")
        ? "custom"
        : "preset",
    });
    
  };
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    try {
      const jobDoc = doc(db, 'jobs', editedJob.id);
      await updateDoc(jobDoc, editedJob);
      setJobs((prevJobs) => prevJobs.map((job) => (job.id === editedJob.id ? { ...editedJob } : job)));
      setIsEditing(false);
      setSelectedJob({ ...editedJob });
    } catch (error) {
      console.error('Error saving job changes:', error);
    }
  };

  const handleCancelEdit = () => setIsEditing(false);

  const markJobCompleted = async () => {
    if (selectedJob) {
      const jobDoc = doc(db, "jobs", selectedJob.id);
      await updateDoc(jobDoc, { status: "Completed" });
      setJobs((prevJobs) => prevJobs.map(j => j.id === selectedJob.id ? { ...j, status: "Completed" } : j));
      closeModal();
      closeVerificationPopup();
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const jobDoc = doc(db, 'jobs', jobId);
      await deleteDoc(jobDoc);
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId));
      closeModal();
      closeDeletePopup();
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSortChange = (e) => setSortOption(e.target.value);

  // ---- NEW core filtering logic ----
  const normalize = (v) => (v || '').toString().toLowerCase();

  const matchesSearch = (job) => {
    const term = normalize(searchTerm);
    if (!term) return true;

    if (searchScopeOnly) {
      return normalize(job.scope).includes(term);
    }

    // Search across useful fields
    return (
      normalize(job.siteName).includes(term) ||
      normalize(job.management).includes(term) ||
      normalize(job.scope).includes(term) ||
      normalize(job.jobDescription).includes(term) ||
      normalize(job.city).includes(term) ||
      normalize(job.address).includes(term) ||
      normalize(job.wo).includes(term) ||
      normalize(job.techAssign).includes(term) ||
      normalize(job.serviceType).includes(term) ||
      normalize(job.status).includes(term)
    );
  };

  const filterAndSortJobs = () => {
    let data = [...jobs];

    // Site filter
    if (selectedSite !== 'ALL_SITES') {
      data = data.filter(j => (j.siteName || '') === selectedSite);
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      if (statusFilter === 'Active') {
        // If your data uses "Active" exactly, this is fine; adjust if needed.
        data = data.filter(j => (j.status || '') === 'Active');
      } else {
        data = data.filter(j => (j.status || '') === statusFilter);
      }
    }

    // Search
    data = data.filter(matchesSearch);

    // Sort
    if (sortOption === 'siteName') {
      data.sort((a, b) => (a.siteName || '').localeCompare(b.siteName || ''));
    } else if (sortOption === 'startDateAsc') {
      data.sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0));
    } else if (sortOption === 'startDateDesc') {
      data.sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
    }

    setFilteredJobs(data);
  };

  const getBackgroundColor = (job) => {
    if (!job.startDate) return 'white';

    const startDate = moment(job.startDate);
    const endDate = job.endDate ? moment(job.endDate) : null;
    const now = moment();
    const diffDaysStart = now.diff(startDate, 'days');
    const diffDaysEnd = endDate ? now.diff(endDate, 'days') : null;

    if ((diffDaysEnd && diffDaysEnd > 1) || (!endDate && diffDaysStart > 1)) {
      return 'red';
    }

    switch (job.serviceType) {
      case 'Proposal': return '#FFC0CB';
      case 'Meeting': return '#98FB98';
      case 'Action Items': return '#ADD8E6';
      case 'Inspection': return '#FFD700';
      default: return 'white';
    }
  };

  return (
    <div className="dashboard-section-container">
      {/* Controls row */}
      <div className="controls-row" style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr 1fr auto' }}>
        {/* Site dropdown */}
        <select
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
          title="Filter by Site"
        >
          {siteOptions.map(opt => (
            <option key={opt} value={opt}>
              {opt === 'ALL_SITES' ? 'All Sites' : opt}
            </option>
          ))}
        </select>

        {/* Status filter */}
        <div className="status-pills" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['ALL', ...statusOptions].map(s => (
            <button
              key={s}
              className={`pill ${statusFilter === s ? 'pill-active' : ''}`}
              onClick={() => setStatusFilter(s)}
              title={`Show ${s === 'ALL' ? 'all statuses' : s}`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Sort */}
        <select value={sortOption} onChange={handleSortChange}>
          <option value="">Sort By</option>
          <option value="siteName">Site Name</option>
          <option value="startDateAsc">Start Date (A–Z)</option>
          <option value="startDateDesc">Start Date (Z–A)</option>
        </select>

        {/* Scope-only toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={searchScopeOnly}
            onChange={(e) => setSearchScopeOnly(e.target.checked)}
          />
          Search scope only
        </label>
      </div>

      {/* Search bar */}
      <div className="search-sort-container" style={{ marginTop: 8 }}>
        <input
          type="text"
          placeholder={searchScopeOnly ? "Search scope…" : "Search site/management/scope/WO/etc…"}
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* Results */}
      <div className="dashboard-section2" style={{ marginTop: 12 }}>
        <h2>
          Jobs
          {selectedSite !== 'ALL_SITES' ? ` – ${selectedSite}` : ''} 
          {statusFilter !== 'ALL' ? ` – ${statusFilter}` : ''} 
          {` (${filteredJobs.length})`}
        </h2>

        {filteredJobs.length > 0 ? (
          <ul>
            {filteredJobs.map((job) => (
              <li key={job.id} style={{ backgroundColor: getBackgroundColor(job) }}>
                <strong>Site Name:</strong> {job.siteName}
                <br />
                <strong>City:</strong> {job.city}
                <br />
                <strong>Start Date:</strong> {job.startDate}
                <br />
                <strong>Service Type:</strong> {job.serviceType}
                <br />
                <strong>Scope:</strong> {job.scope}
                <br />
                <strong>Tech:</strong> {job.techAssign}
                <br />
                <strong>WO#:</strong> {job.wo}
                <br />
                <strong>Status:</strong> {job.status}
                <br />
                <button className="smallazul-button" onClick={() => setSelectedJob(job)}>Details</button>
              </li>
            ))}
          </ul>
        ) : (
          <p>No jobs match your filters.</p>
        )}
      </div>

      {/* ---- Modal: Job Details (unchanged except context) ---- */}
      {selectedJob && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <h2>Job Details</h2>
            <button className="yellow-button" onClick={() => navigate(`/JobChecklist/${selectedJob.id}`)}>
              📝 Checklist
            </button>

            {isEditing ? (
              <>
                <label>
                  Site Name:
                  <input type="text" name="siteName" value={editedJob.siteName || ''} onChange={handleInputChange} />
                </label>
                <label>
                  Work Order:
                  <input type="text" name="wo" value={editedJob.wo || ''} onChange={handleInputChange} />
                </label>
                <label>
                  Address:
                  <input type="text" name="address" value={editedJob.address || ''} onChange={handleInputChange} />
                </label>
                <label>
                  City:
                  <input type="text" name="city" value={editedJob.city || ''} onChange={handleInputChange} />
                </label>
                <label>
                  State:
                  <select name="state" value={editedJob.state || selectedJob.state} onChange={handleInputChange}>
                    <option value="">Select State</option>
                    {states.map(state => (<option key={state} value={state}>{state}</option>))}
                  </select>
                </label>
                <label>
                  Start Date:
                  <input type="date" name="startDate" value={editedJob.startDate || selectedJob.startDate} onChange={handleInputChange} />
                </label>
                <label>
                  End Date:
                  <input type="date" name="endDate" value={editedJob.endDate || selectedJob.endDate} onChange={handleInputChange} />
                </label>
                <label>Recurrence:</label>
<select
  value={editedJob.recurrence?.type || "none"}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        type: e.target.value,
      },
    }))
  }
>
  <option value="none">One-Time</option>
  <option value="daily">Daily</option>
  <option value="weekly">Weekly</option>
  <option value="biweekly">Bi-Weekly</option>
  <option value="monthly">Monthly</option>
  <option value="bimonthly">Bi-Monthly</option>
  <option value="quarterly">Quarterly</option>
  <option value="biyearly">Bi-Yearly</option>
  <option value="yearly">Yearly</option>
  <option value="customDates">Custom Dates</option>
</select>

{editedJob.recurrence?.type === "weekly" && (
  <>
    <label>Days of Week:</label>
    {[0,1,2,3,4,5,6].map(d => (
      <label key={d} style={{ marginRight: 8 }}>
        <input
          type="checkbox"
          checked={editedJob.recurrence?.daysOfWeek?.includes(d)}
          onChange={(e) => {
            const days = editedJob.recurrence.daysOfWeek || [];
            const updated = e.target.checked
              ? [...days, d]
              : days.filter(x => x !== d);

            setEditedJob(prev => ({
              ...prev,
              recurrence: {
                ...prev.recurrence,
                daysOfWeek: updated,
              },
            }));
          }}
        />
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]}
      </label>
    ))}
  </>
)}

{editedJob.recurrence?.type === "customDates" && (
  <>
    <label>Custom Dates (comma separated):</label>
    <input
      type="text"
      value={(editedJob.recurrence.customDates || []).join(",")}
      onChange={(e) =>
        setEditedJob(prev => ({
          ...prev,
          recurrence: {
            ...prev.recurrence,
            customDates: e.target.value
              .split(",")
              .map(d => d.trim())
              .filter(Boolean),
          },
        }))
      }
    />
  </>
)}

                <label>Start Time:</label>
<select
  name="startTime"
  value={editedJob.startTime ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value !== "Custom Time") {
      setEditedJob((prev) => ({
        ...prev,
        startTime: value,
        startTimeMode: "preset",
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        startTime: "",
        startTimeMode: "custom",
      }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((opt) => (
    <option key={opt} value={opt}>{opt}</option>
  ))}
</select>

{/* Show only when Custom Time is selected */}
{editedJob.startTimeMode === "custom" && (
  <input
    type="time"
    value={editedJob.startTime || ""}
    onChange={(e) =>
      setEditedJob((prev) => ({ ...prev, startTime: e.target.value }))
    }
  />
)}
<label>End Time:</label>
<input
  type="time"
  name="endTime"
  value={editedJob.endTime || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({ ...prev, endTime: e.target.value }))
  }
/>

                <label>
                  Service Type:
                  <select name="serviceType" value={editedJob.serviceType || ''} onChange={handleInputChange}>
                    <option value="">Select Service Type</option>
                    {serviceTypes.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </label>
                <label>
                  Scope:
                  <input type="text" name="scope" value={editedJob.scope || ''} onChange={handleInputChange} />
                </label>
                <label>
                  Job Description:
                  <input type="text" name="jobDescription" value={editedJob.jobDescription || ''} onChange={handleInputChange} />
                </label>
                <label>
  Client Phone:
  <input
    type="text"
    name="clientPhone"
    value={editedJob.clientPhone || ''}
    onChange={handleInputChange}
  />
</label>

<label>
  Client Email:
  <input
    type="email"
    name="clientEmail"
    value={editedJob.clientEmail || ''}
    onChange={handleInputChange}
  />
</label>

                <label>
                  Tech Assign:
                  <select name="techAssign" value={editedJob.techAssign || ''} onChange={handleInputChange}>
                    <option value="">Select Technician</option>
                    {usersList.map((u) => (
                      <option key={u.id} value={`${u.firstName} ${u.lastName}`}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Status:
                  <select name="status" value={editedJob.status || ''} onChange={handleInputChange}>
                    <option value="">Select Status</option>
                    {statusOptions.map(s => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </label>
                <label>
                  Notes:
                  <input type="text" name="notes" value={editedJob.notes || ''} onChange={handleInputChange} />
                </label>

                <div className="modal-buttons">
                  <button className='green-button' onClick={handleSaveChanges}>Save</button>
                  <button className='red-button' onClick={handleCancelEdit}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
                <p><strong>Work Order:</strong> {selectedJob.wo}</p>
                <p><strong>Address:</strong> {selectedJob.address}</p>
                <p><strong>City:</strong> {selectedJob.city}</p>
                <p><strong>State:</strong> {selectedJob.state}</p>
                <p><strong>Start Date:</strong> {selectedJob.startDate}</p>
                <p><strong>End Date:</strong> {selectedJob.endDate}</p>
                <p>
  <strong>Time Window:</strong>{" "}
  {selectedJob.startTimeMode === "custom"
    ? `Custom (${selectedJob.startTime})`
    : selectedJob.startTime || "ANY"}
</p>

<p><strong>End Time:</strong> {selectedJob.endTime || "N/A"}</p>
<p>
  <strong>Recurrence:</strong>{" "}
  {selectedJob.recurrence?.type
    ? selectedJob.recurrence.type
    : "One-Time"}
</p>

{selectedJob.recurrence?.type === "weekly" && (
  <p>
    <strong>Days:</strong>{" "}
    {(selectedJob.recurrence.daysOfWeek || [])
      .map(d => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d])
      .join(", ")}
  </p>
)}

{selectedJob.recurrence?.type === "customDates" && (
  <p>
    <strong>Custom Dates:</strong>{" "}
    {(selectedJob.recurrence.customDates || []).join(", ")}
  </p>
)}

                <p><strong>Service Type:</strong> {selectedJob.serviceType}</p>
                <p><strong>Scope:</strong> {selectedJob.scope}</p>
                <p><strong>Job Description:</strong> {selectedJob.jobDescription}</p>
                <p>
  <strong>Client Phone:</strong> {selectedJob.clientPhone || "N/A"}
</p>
<p>
  <strong>Client Email:</strong> {selectedJob.clientEmail || "N/A"}
</p>

                <p><strong>Tech Assign:</strong> {selectedJob.techAssign}</p>
                <p><strong>Status:</strong> {selectedJob.status}</p>

                {selectedJob.proposalPDFs && selectedJob.proposalPDFs.length > 0 && (
                  <div>
                    <strong>Proposals PDF:</strong>
                    <ul>
                      {selectedJob.proposalPDFs.map((pdf, index) => (
                        <li key={index}>
                          <a href={pdf.pdfURL} target="_blank" rel="noopener noreferrer">
                            {pdf.pdfName}
                          </a> ({pdf.dateTimeString})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedJob.files && selectedJob.files.length > 0 && (
                  <div>
                    <strong>Files:</strong>
                    <ul>
                      {selectedJob.files.map((file, index) => (
                        <li key={index}>
                          <a href={file.url} target="_blank" rel="noopener noreferrer">
                            {file.name || `File ${index + 1}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <h3>Notes</h3>
                <div className="notes-container">
                  {Array.isArray(selectedJob.notes) && selectedJob.notes.length > 0 ? (
                    <ul>
                      {selectedJob.notes.map((note, index) => (
                        <li key={index}>
                          <strong>{note.author} ({note.timestamp}):</strong> {note.text}
                          {note.files && note.files.map((file, fileIndex) => (
                            <p key={fileIndex}>
                              <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
                                {/\.(jpeg|jpg|gif|png)$/i.test(file.fileURL)
                                  ? <img src={file.fileURL} alt={`Attachment ${fileIndex + 1}`} className="thumbnail" />
                                  : `View Attachment ${fileIndex + 1}`}
                              </a>
                            </p>
                          ))}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No notes yet.</p>
                  )}
                </div>

                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
                  <div>
                    <h3>Uploaded Files</h3>
                    <div className="uploaded-files">
                      {selectedJob.fileURLs.map((fileURL, index) => (
                        <div key={index}>
                          <a href={fileURL} target="_blank" rel="noopener noreferrer">
                            {/\.(jpeg|jpg|gif|png)$/i.test(fileURL)
                              ? <img src={fileURL} alt={`File ${index + 1}`} className="thumbnail" />
                              : `View File ${index + 1}`}
                          </a>
                          <p>{selectedJob.fileNames && selectedJob.fileNames[index]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="modal-buttons">
                  <button className='grey-button' onClick={handleEditClick}>Edit</button>
                  <button className="azul-button" onClick={() => setShowNotePopup(true)}>Add Note</button>
                  <button className="green-button" onClick={() => setShowVerificationPopup(true)}>Mark Job as Completed</button>
                </div>
                <div className="modal-buttons">
                <button
  className="green-button"
  onClick={() => {
    setTotalInput("");
    setShowTotalPopup(true);
  }}
>
  Add / Change Total
</button>



                  <button className="black-button" onClick={() => setShowDeletePopup(true)}>Delete</button>
                  <button className="red-button" onClick={closeModal}>Close</button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {showNotePopup && (
        <>
          <div className="modal-overlay" onClick={closeNotePopup}></div>
          <div className="modal">
            <h2>Add Note</h2>
            <textarea className="form-container" rows="4" placeholder="Enter your note here..." value={noteText} onChange={(e) => setNoteText(e.target.value)}></textarea>
            <input type="file" accept="image/*, .pdf" multiple onChange={handleFileChange} />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>{uploading ? "Uploading..." : "Save"}</button>
              <button className="red-button" onClick={closeNotePopup}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {showVerificationPopup && (
        <>
          <div className="modal-overlay" onClick={closeVerificationPopup}></div>
          <div className="modal">
            <h3>Are you sure you want to complete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={markJobCompleted}>Yes</button>
              <button className="shortred-button" onClick={closeVerificationPopup}>No</button>
            </div>
          </div>
        </>
      )}

{showTotalPopup && (
  <>
    <div className="modal-overlay" onClick={() => setShowTotalPopup(false)} />
    <div className="modal">
      <h3>Set Job Total</h3>

      <label>
        Total Amount
        <div className="price-input">
          <span>$</span>
          <input
            type="number"
            value={totalInput}
            onChange={(e) => setTotalInput(e.target.value)}
          />
        </div>
      </label>

      <div className="modal-buttons">
        <button
          className="green-button"
          onClick={async () => {
            const parsedTotal = Number(totalInput);

            if (Number.isNaN(parsedTotal) || parsedTotal <= 0) {
              alert("Please enter a valid total amount.");
              return;
            }

            const jobKey = String(selectedJob.wo);

            await addJobTotal({
              jobId: jobKey,
              wo: jobKey,
              proposalId: selectedJob.proposalId || null,
              total: parsedTotal,
              source: "job",
            });

            setShowTotalPopup(false);
          }}
        >
          Save
        </button>

        <button
          className="red-button"
          onClick={() => setShowTotalPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>
)}


      {showDeletePopup && (
        <>
          <div className="modal-overlay" onClick={closeDeletePopup}></div>
          <div className="modal">
            <h3>Are you sure you want to delete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={() => deleteJob(selectedJob.id)}>Yes</button>
              <button className="shortred-button" onClick={closeDeletePopup}>No</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkOrders;
